import { useEffect, useRef } from "react";
import { Eye, Heart } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn, formatCount } from "~/lib/utils";
import { useVideoSource } from "~/lib/hooks/useVideoSource";
import { useVideoWatchTracking } from "~/lib/hooks/useVideoWatchTracking";

export interface VideoSlideData {
  feedItemId?: string;
  videoUrl: string;
  thumbnail?: string;
  title?: string;
  dealId?: string;
  viewCount?: number;
  likeCount?: number;
  // Canonical isEntertainment() classification from the call site (see watch-feed.tsx).
  contentType: "product" | "entertainment";
}

interface VideoSlideProps {
  data: VideoSlideData;
  className?: string;
  // Only enable watch tracking on active-viewing surfaces (mobile full-screen feed,
  // click-to-open fullscreen modal). Autoplaying preview surfaces must leave this false.
  trackWatch?: boolean;
  // Where this video plays, tagged onto watch events (e.g. "watch_feed",
  // "watch_modal", "deal_section_modal"). Only meaningful when trackWatch is true.
  surface?: string;
}

export function VideoSlide({ data, className, trackWatch = false, surface = "watch" }: VideoSlideProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const { notifyEnter } = useVideoWatchTracking(
    videoRef,
    data.feedItemId,
    data.videoUrl,
    data.contentType,
    surface,
    trackWatch
  );

  const { startLoad, stopLoad } = useVideoSource(videoRef, data.videoUrl);

  useEffect(() => {
    const video = videoRef.current;
    const wrapper = wrapperRef.current;
    if (!video || !wrapper) return;

    let playPromise: Promise<void> | null = null;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Same threshold that starts playback, so the analytics funnel's "entered
            // the video" moment lines up exactly with when we try to play it. HLS starts
            // fetching here too — an offscreen slide holding its own MediaSource and first
            // fragment costs far more than the metadata `preload` used to fetch.
            notifyEnter();
            startLoad();
            playPromise = video.play();
            playPromise.catch(e => {
              if (e.name !== "AbortError") console.error("Video play failed:", e);
            });
          } else {
            // The feed keeps every slide mounted, so a player left running here would hold its
            // MediaSource and buffered fragments for the rest of the visit.
            stopLoad();
            if (playPromise) {
              playPromise
                .then(() => {
                  if (!video.paused) video.pause();
                })
                .catch(() => {})
                .finally(() => {
                  playPromise = null;
                });
            } else if (!video.paused) {
              video.pause();
            }
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(wrapper);

    return () => observer.disconnect();
  }, [data.feedItemId, notifyEnter, startLoad, stopLoad]);

  const handleViewDeal = () => {
    if (data.dealId) {
      window.open(`/deal/detail?deal_id=${data.dealId}`, "_blank");
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative h-full w-full bg-black", className)}>
      <video
        ref={videoRef}
        poster={data.thumbnail}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2.5 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pb-6">
        {data.title && <p className="text-sm leading-snug font-medium text-white">{data.title}</p>}

        {data.viewCount !== undefined && (
          <div className="flex items-center gap-1.5 text-white/60">
            <Eye className="size-3" />
            <span className="text-xs">{formatCount(data.viewCount)} views</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          {data.dealId && (
            <Button
              id={`watch-view-deal-${data.dealId}`}
              variant="yellow"
              size="lg"
              className="flex-1"
              onClick={handleViewDeal}
            >
              View Deal
            </Button>
          )}
          {data.likeCount !== undefined && (
            <div className="flex flex-col items-center gap-1">
              <Heart className="size-6 text-white" />
              <span className="text-xs text-white/60">{formatCount(data.likeCount)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
